import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useLiffSession } from '../hooks/useLiff';
import { useAttribution } from '../hooks/useAttribution';
import { apiCall } from '../lib/api';
import { getIdToken } from '../lib/liff';
import { getLiffExtraContext, type LiffExtraContext } from '../lib/liff-context';
import { trackRegister, trackLeadSignup } from '../lib/analytics';
import {
  BUDGET_OPTIONS,
  type RegisterCustomerInput,
  type RegisterCustomerOutput,
  type Gender,
  type BudgetRange,
} from '@meso/types';

const Schema = z.object({
  first_name: z.string().min(1, 'กรุณาพิมพ์ชื่อ').max(100),
  last_name: z.string().max(100).default(''),
  gender: z.enum(['W', 'M', 'N']),
  birth_year: z.string().regex(/^\d{4}$/),
  birth_month: z.string().regex(/^\d{2}$/),
  birth_day: z.string().regex(/^\d{2}$/),
  phone: z.string().regex(/^0\d{9}$/, 'เบอร์ 10 หลัก เริ่มด้วย 0'),
  email: z.string().email('รูปแบบ email ไม่ถูกต้อง').or(z.literal('')).optional(),
  budget_range: z.enum([
    'under_500', '500_1000', '1000_5000', '5000_10000', '10000_30000', 'anything',
  ]),
});
type FormData = z.infer<typeof Schema>;

const thisYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 80 }, (_, i) => String(thisYear - 18 - i));
const monthOptions = [
  ['01', 'มกราคม'], ['02', 'กุมภาพันธ์'], ['03', 'มีนาคม'], ['04', 'เมษายน'],
  ['05', 'พฤษภาคม'], ['06', 'มิถุนายน'], ['07', 'กรกฎาคม'], ['08', 'สิงหาคม'],
  ['09', 'กันยายน'], ['10', 'ตุลาคม'], ['11', 'พฤศจิกายน'], ['12', 'ธันวาคม'],
] as const;
const dayOptions = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));

export function Register() {
  const { profile } = useLiffSession();
  const attribution = useAttribution();
  const navigate = useNavigate();
  const [liffCtx, setLiffCtx] = useState<LiffExtraContext | null>(null);

  useEffect(() => {
    getLiffExtraContext().then(setLiffCtx).catch(() => {/* ignore */});
  }, []);

  const { register, handleSubmit, formState: { errors, isSubmitting }, setValue } = useForm<FormData>({
    resolver: zodResolver(Schema),
    defaultValues: {
      first_name: profile?.displayName?.split(' ')[0] ?? '',
      last_name: profile?.displayName?.split(' ').slice(1).join(' ') ?? '',
      gender: 'W',
      birth_year: String(thisYear - 25),
      birth_month: '01',
      birth_day: '01',
      budget_range: '1000_5000',
    },
  });

  useEffect(() => {
    if (liffCtx?.email) setValue('email', liffCtx.email, { shouldValidate: false });
  }, [liffCtx, setValue]);

  const submit = useMutation({
    mutationFn: async (form: FormData) => {
      const body: RegisterCustomerInput = {
        idToken: getIdToken() ?? '',
        first_name: form.first_name,
        last_name: form.last_name || '',
        gender: form.gender as Gender,
        birthday: `${form.birth_year}-${form.birth_month}-${form.birth_day}`,
        phone: form.phone,
        email: form.email || undefined,
        budget_range: form.budget_range as BudgetRange,
        source: {
          channel: attribution.channel || 'direct',
          campaign:      attribution.campaign ?? undefined,
          variant:       attribution.variant ?? undefined,
          referrer:      attribution.referrer ?? undefined,
          fbclid:        attribution.fbclid ?? undefined,
          gclid:         attribution.gclid ?? undefined,
          ttclid:        attribution.ttclid ?? undefined,
          line_click_id: attribution.line_click_id ?? undefined,
          utm_content:   attribution.utm_content ?? undefined,
          utm_term:      attribution.utm_term ?? undefined,
        },
        line_profile: liffCtx ? {
          status_message: liffCtx.status_message,
          language: liffCtx.language,
          os: liffCtx.os,
          friend_status: liffCtx.friend_status,
        } : undefined,
      };
      return apiCall<RegisterCustomerOutput>('post', '/customers', body);
    },
    onSuccess: (res, form) => {
      // Legacy register event (kept for any existing GA tags)
      trackRegister(attribution.channel || 'direct', attribution.campaign);

      // NEW: GTM-ready lead_signup event
      const opt = BUDGET_OPTIONS.find((b) => b.value === form.budget_range);
      trackLeadSignup({
        lead_id:        String((res as any)?.customer?.id ?? (res as any)?.id ?? ''),
        budget_range:   form.budget_range,
        signup_value:   opt?.signupValue ?? 0,
        currency:       'THB',
        source_channel: attribution.channel || 'direct',
        phone:          form.phone,
        email:          form.email || undefined,
      });

      navigate('/dashboard', { replace: true });
    },
  });

  return (
    <div className="max-w-md mx-auto px-4 py-6">
      <div className="flex items-center justify-center mb-3">
        <div className="text-xl font-bold tracking-tight text-black">mesoestetic</div>
      </div>
      <h1 className="text-xl font-bold mb-4">ลงทะเบียนสมาชิก</h1>

      {profile?.pictureUrl && (
        <div className="flex items-center gap-3 mb-4">
          <img src={profile.pictureUrl} alt="" className="w-14 h-14 rounded-full" />
          <div>
            <div className="font-semibold">{profile.displayName}</div>
            <div className="text-xs text-gray-500">เข้าสู่ระบบด้วย LINE</div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit((d) => submit.mutate(d))} className="space-y-4">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="form-label">ชื่อ *</label>
            <input className="form-input" {...register('first_name')} />
            {errors.first_name && <div className="form-error">{errors.first_name.message}</div>}
          </div>
          <div>
            <label className="form-label">นามสกุล</label>
            <input className="form-input" {...register('last_name')} />
          </div>
        </div>

        <div>
          <label className="form-label">เพศ *</label>
          <select className="form-input" {...register('gender')}>
            <option value="W">หญิง</option>
            <option value="M">ชาย</option>
            <option value="N">ไม่ต้องการตอบ</option>
          </select>
        </div>

        <div>
          <label className="form-label">วันเกิด *</label>
          <div className="grid grid-cols-3 gap-2">
            <select className="form-input" {...register('birth_day')}>
              {dayOptions.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <select className="form-input" {...register('birth_month')}>
              {monthOptions.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select>
            <select className="form-input" {...register('birth_year')}>
              {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="form-label">เบอร์โทรศัพท์ *</label>
          <input className="form-input" inputMode="numeric" maxLength={10} {...register('phone')} />
          {errors.phone && <div className="form-error">{errors.phone.message}</div>}
        </div>

        <div>
          <label className="form-label">อีเมล (ไม่บังคับ)</label>
          <input className="form-input" type="email" {...register('email')} />
          {errors.email && <div className="form-error">{errors.email.message}</div>}
        </div>

        {/* NEW — งบประมาณรักษาฝ้า (replaces จังหวัด) */}
        <div>
          <label className="form-label">งบประมาณในการรักษาฝ้า *</label>
          <select className="form-input" {...register('budget_range')}>
            {BUDGET_OPTIONS.map((b) => (
              <option key={b.value} value={b.value}>{b.label}</option>
            ))}
          </select>
          {errors.budget_range && <div className="form-error">{errors.budget_range.message}</div>}
        </div>

        {submit.isError && (
          <div className="text-red-600 text-sm">{(submit.error as Error).message}</div>
        )}

        <button type="submit" disabled={isSubmitting || submit.isPending}
                className="btn btn-primary btn-block">
          {submit.isPending ? 'กำลังบันทึก...' : 'สมัครสมาชิก'}
        </button>
      </form>
    </div>
  );
}
